import { useState, useEffect } from "react";
import { ContactsPage } from "./contacts";
import { Icon, PROVIDERS, api } from "../lib/core";

export const ScheduledRechargesPage = ({ onBack, dataPlans }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await api.get("/scheduled-recharges");
      setSchedules(response.data);
    } catch (err) {
      console.error("Failed to fetch schedules:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (scheduleId) => {
    try {
      const response = await api.put(`/scheduled-recharges/${scheduleId}/toggle`);
      setSchedules(schedules.map(s => 
        s.id === scheduleId ? { ...s, is_active: response.data.is_active } : s
      ));
      setMessage({ type: "success", text: response.data.message });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to toggle schedule" });
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm("Delete this scheduled recharge?")) return;
    
    try {
      await api.delete(`/scheduled-recharges/${scheduleId}`);
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      setMessage({ type: "success", text: "Schedule deleted" });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to delete schedule" });
    }
  };

  const getFrequencyText = (schedule) => {
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    if (schedule.frequency === "once") return "One-time";
    if (schedule.frequency === "daily") return `Daily at ${schedule.time_of_day}`;
    if (schedule.frequency === "weekly") return `Every ${days[schedule.day_of_week]} at ${schedule.time_of_day}`;
    if (schedule.frequency === "monthly") return `Monthly on day ${schedule.day_of_month} at ${schedule.time_of_day}`;
    return schedule.frequency;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <header className="bg-primary-custom text-white sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <Icon name="arrow_back" />
          </button>
          <span className="font-bold text-xl flex-1">Scheduled Recharges</span>
          <button
            data-testid="create-schedule-btn"
            onClick={() => setShowCreateModal(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Icon name="add" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-4 pb-24">
        {/* Message */}
        {message.text && (
          <div className={`px-4 py-3 rounded-xl flex items-center gap-2 ${
            message.type === "success" 
              ? "bg-green-50 border border-green-200 text-green-700" 
              : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            <Icon name={message.type === "success" ? "check_circle" : "error"} />
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: "", text: "" })} className="ml-auto">
              <Icon name="close" className="text-sm" />
            </button>
          </div>
        )}

        {schedules.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-primary-custom/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="schedule" className="text-primary-custom text-4xl" />
            </div>
            <h3 className="font-bold text-slate-900 text-lg">No Scheduled Recharges</h3>
            <p className="text-slate-500 mt-2">Set up automatic or reminder-based recharges.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 px-6 py-3 bg-primary-custom text-white rounded-xl font-semibold flex items-center gap-2 mx-auto"
            >
              <Icon name="add" />
              <span>Create Schedule</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => {
              const providerData = PROVIDERS.find(p => p.name === schedule.provider);
              const scale = providerData?.logoScale || 1;
              const logoSize = Math.round(32 * scale);
              return (
                <div
                  key={schedule.id}
                  className={`bg-white rounded-2xl overflow-hidden border ${
                    schedule.is_active ? "border-slate-200" : "border-slate-100 opacity-60"
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: providerData?.color || "#666" }}>
                        {providerData?.logo ? (
                          <img src={providerData.logo} alt={schedule.provider} className="object-contain bg-white rounded-full p-1" style={{ width: `${logoSize}px`, height: `${logoSize}px` }} />
                        ) : (
                          <span className="font-bold text-white text-sm">{schedule.provider?.substring(0, 2)}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-slate-900">{schedule.nickname}</p>
                        <p className="text-sm text-slate-500">{schedule.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary-custom">₦{schedule.amount.toLocaleString()}</p>
                        <p className="text-xs text-slate-500 capitalize">{schedule.bundle_type}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-sm">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        schedule.schedule_type === "auto" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-blue-100 text-blue-700"
                      }`}>
                        <Icon name={schedule.schedule_type === "auto" ? "autorenew" : "notifications"} className="text-sm mr-1 inline" />
                        {schedule.schedule_type === "auto" ? "Auto" : "Reminder"}
                      </div>
                      <span className="text-slate-500">{getFrequencyText(schedule)}</span>
                    </div>

                    {schedule.next_execution && schedule.is_active && (
                      <div className="mt-2 text-xs text-slate-400">
                        Next: {new Date(schedule.next_execution).toLocaleString()}
                      </div>
                    )}
                  </div>

                  <div className="px-4 py-3 bg-slate-50 flex gap-2 border-t border-slate-100">
                    <button
                      onClick={() => handleToggle(schedule.id)}
                      className={`flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 ${
                        schedule.is_active 
                          ? "bg-amber-100 text-amber-700" 
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      <Icon name={schedule.is_active ? "pause" : "play_arrow"} className="text-base" />
                      <span>{schedule.is_active ? "Pause" : "Resume"}</span>
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      className="py-2 px-4 rounded-lg bg-red-100 text-red-700 font-medium text-sm flex items-center gap-1"
                    >
                      <Icon name="delete" className="text-base" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Schedule Modal */}
      {showCreateModal && (
        <CreateScheduleModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(newSchedule) => {
            setSchedules([newSchedule, ...schedules]);
            setShowCreateModal(false);
            setMessage({ type: "success", text: "Schedule created!" });
          }}
          dataPlans={dataPlans}
        />
      )}
    </div>
  );
};

// Create Schedule Modal Component

export const CreateScheduleModal = ({ onClose, onCreated, dataPlans }) => {
  const [phone, setPhone] = useState("");
  const [nickname, setNickname] = useState("");
  const [provider, setProvider] = useState("");
  const [amount, setAmount] = useState("");
  const [bundleType, setBundleType] = useState("airtime");
  const [selectedSubcode, setSelectedSubcode] = useState("");
  const [scheduleType, setScheduleType] = useState("reminder");
  const [frequency, setFrequency] = useState("weekly");
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [contactId, setContactId] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const _validityDays = (desc) => {
    const s = (desc || "").toLowerCase();
    if (/\bdaily\b|\b1\s*day\b/.test(s)) return 1;
    const m = s.match(/(\d+)\s*(day|week|month|year)/);
    if (m) { const n = +m[1]; const u = m[2]; return u === "day" ? n : u === "week" ? n * 7 : u === "month" ? n * 30 : n * 365; }
    if (/\bweekly\b/.test(s)) return 7;
    if (/\bmonthly\b/.test(s)) return 30;
    return 999;
  };
  const providerPlans = [...(dataPlans[provider] || [])].sort(
    (a, b) => (_validityDays(a.description) - _validityDays(b.description)) || ((a.amount || 0) - (b.amount || 0))
  );
  // AM/PM time picker helpers (timeOfDay stays "HH:MM" 24h for the backend)
  const _h24 = parseInt((timeOfDay || "09:00").split(":")[0], 10) || 0;
  const _min = (timeOfDay || "09:00").split(":")[1] || "00";
  const _ampm = _h24 >= 12 ? "PM" : "AM";
  const _h12 = _h24 % 12 || 12;
  const setTime = (h12, min, ap) => {
    let h = (h12 % 12) + (ap === "PM" ? 12 : 0);
    setTimeOfDay(`${String(h).padStart(2, "0")}:${min}`);
  };

  const handleCreate = async () => {
    if (!phone || !provider || !amount) return;

    setLoading(true);
    try {
      const response = await api.post("/scheduled-recharges", {
        phone,
        nickname: nickname || phone,
        provider,
        amount: parseFloat(amount),
        bundle_type: bundleType,
        subcode: bundleType === "data" ? selectedSubcode : null,
        schedule_type: scheduleType,
        frequency,
        day_of_week: frequency === "weekly" ? dayOfWeek : null,
        day_of_month: frequency === "monthly" ? dayOfMonth : null,
        time_of_day: timeOfDay,
        contact_id: contactId
      });
      onCreated(response.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create schedule");
    } finally {
      setLoading(false);
    }
  };

  if (showContactPicker) {
    return (
      <ContactsPage
        pickMode
        onBack={() => setShowContactPicker(false)}
        onPick={({ phone: p, provider: prov, name, contact_id }) => {
          setPhone(p);
          if (prov) setProvider(prov);
          if (name && !nickname) setNickname(name);
          setContactId(contact_id || null);
          setShowContactPicker(false);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl text-slate-900">Schedule Recharge</h2>
            <p className="text-sm text-slate-500">Step {step} of 2</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {step === 1 ? (
            <>
              {/* Pick from Contacts */}
              <button
                type="button"
                onClick={() => setShowContactPicker(true)}
                data-testid="schedule-pick-contact-btn"
                className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-primary-custom/40 text-primary-custom font-semibold hover:bg-primary-custom/5 transition-colors"
              >
                <Icon name="contacts" /> Pick from Contacts
              </button>

              {/* Recipient Details */}
              <div>
                <label className="text-slate-700 text-sm font-semibold">Nickname (optional)</label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="e.g., Mom, Office"
                  className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
                />
              </div>

              <div>
                <label className="text-slate-700 text-sm font-semibold">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setContactId(null); }}
                  placeholder="08012345678"
                  className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
                />
              </div>

              <div>
                <label className="text-slate-700 text-sm font-semibold">Network Provider</label>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                  {PROVIDERS.map((p) => {
                    const scale = p.logoScale || 1;
                    const logoSize = Math.round(24 * scale);
                    return (
                      <button
                        key={p.name}
                        onClick={() => setProvider(p.name)}
                        className={`p-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                          provider === p.name ? "border-primary-custom" : "border-slate-200"
                        }`}
                        style={{ backgroundColor: provider === p.name ? p.color : "white" }}
                      >
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center overflow-hidden">
                          <img src={p.logo} alt={p.name} className="object-contain" style={{ width: `${logoSize}px`, height: `${logoSize}px` }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-slate-700 text-sm font-semibold">Bundle Type</label>
                <div className="flex gap-2 mt-1.5">
                  <button
                    onClick={() => setBundleType("airtime")}
                    className={`flex-1 py-3 rounded-xl font-medium ${
                      bundleType === "airtime" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    Airtime
                  </button>
                  <button
                    onClick={() => setBundleType("data")}
                    className={`flex-1 py-3 rounded-xl font-medium ${
                      bundleType === "data" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    Data
                  </button>
                </div>
              </div>

              <div>
                <label className="text-slate-700 text-sm font-semibold">Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none text-lg font-bold"
                />
                <div className="flex gap-2 mt-2">
                  {[100, 200, 500, 1000].map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setAmount(amt.toString())}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                        amount === amt.toString() ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      ₦{amt}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!phone || !provider || !amount}
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <span>Next</span>
                <Icon name="arrow_forward" />
              </button>
            </>
          ) : (
            <>
              {/* Schedule Settings */}
              <div>
                <label className="text-slate-700 text-sm font-semibold">Schedule Type</label>
                <div className="flex gap-2 mt-1.5">
                  <button
                    onClick={() => setScheduleType("reminder")}
                    className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                      scheduleType === "reminder" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    <Icon name="notifications" />
                    <span>Reminder</span>
                  </button>
                  <button
                    onClick={() => setScheduleType("auto")}
                    className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                      scheduleType === "auto" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    <Icon name="autorenew" />
                    <span>Auto</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {scheduleType === "reminder" 
                    ? "You'll receive a notification to recharge" 
                    : "Recharge will happen automatically from wallet"}
                </p>
              </div>

              <div>
                <label className="text-slate-700 text-sm font-semibold">Frequency</label>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                  {[
                    { value: "once", label: "Once" },
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "Monthly" }
                  ].map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setFrequency(f.value)}
                      className={`py-2.5 rounded-xl font-medium text-sm ${
                        frequency === f.value ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {frequency === "weekly" && (
                <div>
                  <label className="text-slate-700 text-sm font-semibold">Day of Week</label>
                  <select
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
                    className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
                  >
                    {DAYS.map((day, i) => (
                      <option key={day} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {frequency === "monthly" && (
                <div>
                  <label className="text-slate-700 text-sm font-semibold">Day of Month</label>
                  <select
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(parseInt(e.target.value))}
                    className="w-full h-12 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none"
                  >
                    {[...Array(28)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-slate-700 text-sm font-semibold">Time</label>
                <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="schedule-time-picker">
                  <select value={_h12} onChange={(e) => setTime(Number(e.target.value), _min, _ampm)} data-testid="schedule-hour"
                    className="h-12 px-3 rounded-xl border border-slate-200 focus:border-primary-custom outline-none">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  <select value={_min} onChange={(e) => setTime(_h12, e.target.value, _ampm)} data-testid="schedule-minute"
                    className="h-12 px-3 rounded-xl border border-slate-200 focus:border-primary-custom outline-none">
                    {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={_ampm} onChange={(e) => setTime(_h12, _min, e.target.value)} data-testid="schedule-ampm"
                    className="h-12 px-3 rounded-xl border border-slate-200 focus:border-primary-custom outline-none">
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <p className="font-semibold text-slate-900">Summary</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>• {nickname || phone} ({phone})</p>
                  <p>• {provider} {bundleType} - ₦{parseFloat(amount || 0).toLocaleString()}</p>
                  <p>• {scheduleType === "auto" ? "Auto-recharge" : "Reminder"}</p>
                  <p>• {frequency === "once" ? "One-time" : frequency === "daily" ? "Every day" : frequency === "weekly" ? `Every ${DAYS[dayOfWeek]}` : `Monthly on day ${dayOfMonth}`} at {timeOfDay}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 h-14 bg-slate-100 text-slate-700 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
                >
                  <Icon name="arrow_back" />
                  <span>Back</span>
                </button>
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="flex-1 h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="schedule" />}
                  <span>Create</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Notifications Dropdown Component
